import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Link,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { HelpOutlined, KeyboardArrowDownOutlined } from "@mui-symbols-material/w300"
import type { FC, ReactElement, ReactNode } from "react"
import type { ControllerRenderProps } from "react-hook-form"
import { Controller, useFormContext } from "react-hook-form"

import { ProductShelfLifeUom } from "../../types"
import type { TechnicalInformationFormData } from "../schemas/TechnicalInformationSchema"

import { FIELD_LABEL_CLASSES, TEXT_FIELD_STYLING_PROPS } from "@/constants/forms"

export const TechnicalInformationContent: FC = () => {
  const { control, watch } = useFormContext<TechnicalInformationFormData>()

  const handleNumericFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: ControllerRenderProps<TechnicalInformationFormData>
  ) => {
    const value = e.target.value
    field.onChange(value === "" ? "" : Number(value))
  }

  return (
    <form>
      <Box>
        <Controller
          name='chemicalName'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box className='relative'>
              <label className={`${FIELD_LABEL_CLASSES} -top-6 mb-[7px] mt-6`}>Chemical Name</label>
              <TextField
                {...field}
                type='text'
                placeholder='Chemical Name'
                fullWidth
                variant='outlined'
                error={!!error}
                helperText={error?.message}
              />
            </Box>
          )}
        />
        <Controller
          name='specificGravity'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box className='mt-6'>
              <label className={FIELD_LABEL_CLASSES}>Specific Gravity</label>
              <TextField
                {...field}
                {...TEXT_FIELD_STYLING_PROPS}
                type='number'
                value={field.value}
                slotProps={{
                  input: {
                    endAdornment: <HelpOutlined />,
                    inputProps: {
                      className: "hide-stepper",
                      min: "0",
                      max: "99",
                      step: "0.00001",
                    },
                  },
                }}
                placeholder='0.00000'
                error={!!error}
                helperText={error?.message}
                onChange={(e) => handleNumericFieldChange(e, field)}
              />
            </Box>
          )}
        />
        <Controller
          name='shelfLifeDays'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Box className='mt-6'>
              <label className={FIELD_LABEL_CLASSES}>Shelf Life</label>
              <TextField
                {...field}
                type='number'
                value={field.value}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Box className='flex items-center gap-4'>
                        <HelpOutlined />
                        <Controller
                          name='shelfLifeUom'
                          control={control}
                          render={({ field }) => (
                            <FormControl>
                              <Select
                                sx={{
                                  "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    border: "none",
                                  },
                                  minWidth: "60px",
                                  "& .MuiSelect-select": {
                                    padding: "2px 4px",
                                  },
                                }}
                                {...field}
                                displayEmpty
                                IconComponent={KeyboardArrowDownOutlined}
                                MenuProps={{
                                  PaperProps: {
                                    style: { maxHeight: 48 * 4.5 + 8, width: "88px" },
                                  },
                                }}
                              >
                                {Object.keys(ProductShelfLifeUom).map((uom) => (
                                  <MenuItem key={uom} value={uom}>
                                    {uom}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Box>
                    ),
                    classes: {
                      input:
                        "p-0 border-0 ring-0 outline-0 focus:border-0 focus:ring-0 focus:outline-none h-6 px-4 py-2",
                    },
                    inputProps: {
                      className: "hide-stepper",
                    },
                  },
                }}
                placeholder='00'
                fullWidth
                variant='outlined'
                error={!!error}
                helperText={error?.message}
                onChange={(e) => handleNumericFieldChange(e, field)}
              />
            </Box>
          )}
        />
        <TextFieldComponent
          control={control}
          name='harmonizedTariffSchedule'
          label='HTS'
          placeholder='000'
          tooltipText={
            <>
              HTS sets out the tariff rates and statistical categories for merchandise imported into the United States.
              <TooltipLink href='https://hts.usitc.gov/' text='More Info' />
            </>
          }
        />
        <Controller
          name='usmcaClass'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={Boolean(watch("usmcaClass"))}
                  role='checkbox'
                  onChange={(event) => field.onChange(event.target.checked)}
                  name='usmcaClass'
                />
              }
              className='mb-1 mt-[27px]'
              label={
                <Box className='flex gap-3'>
                  USMCA (NAFTA)
                  <Tooltip
                    title={
                      <>
                        Determine whether your goods qualify for USMCA
                        <TooltipLink
                          href='https://www.braumillerlaw.com/chemicals-and-the-usmca-its-not-just-how-you-mix-it-to-get-a-reaction-or-not-from-cbp/'
                          text='HERE'
                        />
                      </>
                    }
                  >
                    <HelpOutlined />
                  </Tooltip>
                </Box>
              }
            />
          )}
        />
      </Box>
    </form>
  )
}

export const TooltipLink = ({ text, href }: { text: string; href: string }): ReactElement => (
  <Link
    href={href}
    target='_blank'
    rel='noopener'
    className='ml-1 text-[#ACE1FF] decoration-[#ACE1FF]'
    underline='always'
  >
    {text}
  </Link>
)

interface TextFieldComponentProps {
  name: string
  type?: string
  label: string
  placeholder?: string
  control: any
  tooltipText?: ReactNode
  required?: boolean
  onChange?: (e: any, field: any) => void
}

export const TextFieldComponent: FC<TextFieldComponentProps> = (props: TextFieldComponentProps) => {
  const { name, type = "text", control, placeholder, label, tooltipText, onChange } = props

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Box className='mt-6'>
          <label className={FIELD_LABEL_CLASSES}>{label}</label>
          <TextField
            {...field}
            type={type}
            value={field.value ?? ""}
            slotProps={{
              input: {
                endAdornment: tooltipText ? (
                  <Tooltip
                    title={
                      <Typography className='m-1 w-[155px] text-xs font-medium text-white'>{tooltipText}</Typography>
                    }
                  >
                    <HelpOutlined />
                  </Tooltip>
                ) : (
                  <HelpOutlined />
                ),
                inputProps: {
                  className: `${type === "number" ? "hide-stepper" : ""}`,
                },
              },
            }}
            placeholder={placeholder}
            fullWidth
            variant='outlined'
            error={!!error}
            helperText={error?.message}
            onChange={(e) => {
              field.onChange(onChange ? onChange(e, field) : e.target.value)
            }}
          />
        </Box>
      )}
    />
  )
}
